/**
 * profileStore.js — Feb 2026 · single source of truth for the
 * learner profile.
 *
 * Consolidates every "who is this child" read/write in the app —
 * age, tier, curriculum, country, child name, active child id —
 * into one hookable store. Under the hood we still write to the
 * existing localStorage keys for back-compat (SCOPED_KEYS in
 * children.js, "we_age_group", "mwq_curriculum", etc.), but every
 * component should now read via `useProfile()` / `getActiveProfile()`
 * so headers, LearnTopics year selectors, MyLearning greetings, plan
 * defaults and mastery paths all agree.
 *
 * Tier derivation (spec):
 *   ages 4-6   → "seedlings"
 *   ages 7-10  → "explorers"  (canonical: lib/canonical.js AGE_BANDS;
 *                              their tier is explicitly set; ambiguous
 *                              ages default down)
 *   ages 11-15 → "pioneers"
 */
import { useEffect, useState } from "react";
import { getActiveChild, listChildren, updateChild, switchToChild } from "@/lib/children";

const K_CURRICULUM = "mwq_curriculum";
const K_COUNTRY    = "we_birth_country_code";
const K_AGE        = "we_age_group";

function _safeGet(k) { try { return localStorage.getItem(k); } catch { return null; } }
function _safeSet(k, v) { try { localStorage.setItem(k, v); } catch { /* ignore */ } }

function _emit() {
  try { window.dispatchEvent(new CustomEvent("we-profile-changed")); }
  catch { /* SSR / tests */ }
}

/** Derive tier from a numeric age. Returns "seedlings" | "explorers" | "pioneers". */
export function deriveTier(age) {
  const a = Number(age);
  if (!Number.isFinite(a)) return "explorers";
  if (a <= 6) return "seedlings";
  if (a <= 10) return "explorers";
  return "pioneers";
}

/** Read the current profile synchronously. Never throws.
 *  Always returns a fully-populated object so callers can destructure
 *  without null-guarding every field. */
export function getActiveProfile() {
  const child = getActiveChild() || null;
  // Age precedence: active child.age → we_age_group numeric → default 8
  const ageFromChild = child?.age;
  const ageFromLS = Number(_safeGet(K_AGE));
  const age = Number.isFinite(ageFromChild)
    ? Number(ageFromChild)
    : (Number.isFinite(ageFromLS) && ageFromLS > 0 ? ageFromLS : 8);
  const tier = child?.tier || deriveTier(age);
  const name = child?.name || _safeGet("we_kid_name") || "";
  const country = child?.country || _safeGet(K_COUNTRY) || "";
  const curriculum = child?.curriculum || _safeGet(K_CURRICULUM) || "uk-ks2";
  return {
    childId: child?.id || null,
    name,
    age,
    tier,
    country,
    curriculum,
    dailyMinutes: child?.dailyMinutes || 15,
    subjects: child?.subjects || [],
    hasChild: !!child,
  };
}

/** React hook: subscribe to profile changes. Rebroadcasts every
 *  window `we-profile-changed` / `we-children-changed` / storage
 *  event so headers refresh in sync with LearnTopics / plan. */
export function useProfile() {
  const [profile, setProfile] = useState(getActiveProfile);
  useEffect(() => {
    const refresh = () => setProfile(getActiveProfile());
    const events = [
      "we-profile-changed",
      "we-children-changed",
      "we-active-child-changed",
      "storage",
    ];
    events.forEach((e) => window.addEventListener(e, refresh));
    return () => events.forEach((e) => window.removeEventListener(e, refresh));
  }, []);
  return profile;
}

/** Update the active profile. Writes to the active child record when
 *  one exists, or to the legacy localStorage keys when running in
 *  guest mode. Emits `we-profile-changed`. */
export function updateProfile(patch) {
  if (!patch || typeof patch !== "object") return;
  const child = getActiveChild();
  if (child) {
    const next = { ...child };
    if ("age" in patch)        next.age = Number(patch.age) || child.age;
    if ("name" in patch)       next.name = String(patch.name || "");
    if ("country" in patch)    next.country = String(patch.country || "");
    if ("curriculum" in patch) next.curriculum = String(patch.curriculum || "");
    if ("subjects" in patch)   next.subjects = Array.isArray(patch.subjects) ? patch.subjects : child.subjects;
    if ("dailyMinutes" in patch) next.dailyMinutes = Number(patch.dailyMinutes) || child.dailyMinutes;
    // Recompute tier when age changes.
    if ("age" in patch) next.tier = deriveTier(next.age);
    updateChild(child.id, next);
  } else {
    // Guest mode — write to the flat localStorage keys.
    if (patch.age != null)        _safeSet(K_AGE, String(Math.max(4, Math.min(15, Number(patch.age) || 8))));
    if (patch.name != null)       _safeSet("we_kid_name", String(patch.name));
    if (patch.country != null)    _safeSet(K_COUNTRY, String(patch.country));
    if (patch.curriculum != null) _safeSet(K_CURRICULUM, String(patch.curriculum));
  }
  _emit();
}

/** Convenience — swap the active child by id and fire the event. */
export function selectChild(childId) {
  switchToChild(childId);
  _emit();
}

/** Hook returning just the tier string. Useful in components that
 *  only need the tier to select a skin / theme. */
export function useTier() {
  const p = useProfile();
  return p.tier;
}

/** List every child on this device, plus the id of the active one. */
export function useChildren() {
  const [state, setState] = useState(() => ({
    children: listChildren(),
    activeId: getActiveProfile().childId,
  }));
  useEffect(() => {
    const refresh = () => setState({
      children: listChildren(),
      activeId: getActiveProfile().childId,
    });
    ["we-profile-changed", "we-children-changed", "we-active-child-changed", "storage"]
      .forEach((e) => window.addEventListener(e, refresh));
    return () => {
      ["we-profile-changed", "we-children-changed", "we-active-child-changed", "storage"]
        .forEach((e) => window.removeEventListener(e, refresh));
    };
  }, []);
  return state;
}

/** Dev-only sanity checker — logs a warning if a component derives
 *  the child's age from localStorage directly instead of via this
 *  store. Wrap any hand-rolled `parseInt(localStorage.getItem(...))`
 *  call in this so we can spot drift. */
export function assertProfileScope(component, fieldRead) {
  if (process.env.NODE_ENV === "production") return;
  console.warn(
    `[profileStore] "${component}" is reading "${fieldRead}" directly; ` +
    "prefer useProfile() so header + LearnTopics + plan stay in sync.",
  );
}
